/**
 * Latency measurement (Kevin, 2026-10-26): when the prospect's voice actually
 * reaches this browser after the server released them, so the logs give the
 * real time from "the prospect picks up" to "the caller hears them".
 *
 * On call:bridged the browser watches the decoded audio it plays (an
 * AnalyserNode on the remote stream, i.e. after the jitter buffer) and keeps
 * the first frame above a speech level. It also reads the audio path of the
 * peer connection (round trip to Asterisk, jitter buffer, output latency) and
 * the offset of its clock against the server's, then reports all of it once.
 * Measurement only: it never throws and never touches the call.
 */
import { api } from '@/api/api-client';

export interface AudioCall {
  remoteStream(): MediaStream | null;
  peerConnection(): RTCPeerConnection | null;
}

/** RMS of a frame of speech, above line noise and comfort noise. */
export const SPEECH_RMS = 0.02;
const POLL_MS = 10;
const WAIT_MS = 8000;

export function isAudioCall(call: unknown): call is AudioCall {
  return !!call && typeof (call as AudioCall).remoteStream === 'function' && typeof (call as AudioCall).peerConnection === 'function';
}

export function rms(frame: Float32Array): number {
  let sum = 0;
  for (const v of frame) sum += v * v;
  return frame.length ? Math.sqrt(sum / frame.length) : 0;
}

/** First time the played audio crosses SPEECH_RMS, on this browser's clock, or null. */
async function firstSound(stream: MediaStream, ctx: AudioContext): Promise<number | null> {
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  source.connect(analyser);
  const frame = new Float32Array(analyser.fftSize);
  const started = Date.now();
  try {
    return await new Promise<number | null>((resolve) => {
      const timer = setInterval(() => {
        analyser.getFloatTimeDomainData(frame);
        if (rms(frame) >= SPEECH_RMS) { clearInterval(timer); resolve(Date.now()); return; }
        if (Date.now() - started > WAIT_MS) { clearInterval(timer); resolve(null); }
      }, POLL_MS);
    });
  } finally {
    try { source.disconnect(); } catch { /* gone */ }
  }
}

/** Round trip to Asterisk and mean jitter buffer, in ms, from the peer connection's stats. */
export function audioPath(stats: RTCStatsReport): { rttMs?: number; jitterBufferMs?: number } {
  let rttMs: number | undefined;
  let jitterBufferMs: number | undefined;
  stats.forEach((r: any) => {
    if (r.type === 'candidate-pair' && r.nominated && typeof r.currentRoundTripTime === 'number') rttMs = Math.round(r.currentRoundTripTime * 1000);
    if (r.type === 'inbound-rtp' && r.kind === 'audio' && r.jitterBufferEmittedCount > 0) {
      jitterBufferMs = Math.round((r.jitterBufferDelay / r.jitterBufferEmittedCount) * 1000);
    }
  });
  return { rttMs, jitterBufferMs };
}

/** This browser's clock minus the server's, and its uncertainty (half the round trip). */
async function clockOffset(callId: string): Promise<{ offsetMs: number; offsetErrMs: number } | null> {
  const t0 = Date.now();
  const res = await api.functions.invoke('twilioAudioTiming', { callId, stage: 'clock' }) as { data?: { serverAt?: number } };
  const t1 = Date.now();
  const serverAt = res?.data?.serverAt;
  if (typeof serverAt !== 'number') return null;
  return { offsetMs: Math.round((t0 + t1) / 2 - serverAt), offsetErrMs: Math.round((t1 - t0) / 2) };
}

export async function measureHeard(callId: string, call: unknown, bridgedAt: number): Promise<void> {
  try {
    if (!callId || !isAudioCall(call) || typeof AudioContext === 'undefined') return;
    const stream = call.remoteStream();
    if (!stream) return;
    const ctx = new AudioContext();
    try {
      if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
      const [heardAt, clock] = await Promise.all([firstSound(stream, ctx), clockOffset(callId).catch(() => null)]);
      const pc = call.peerConnection();
      const path = pc ? audioPath(await pc.getStats()) : {};
      const outputMs = Math.round(((ctx as AudioContext & { outputLatency?: number }).outputLatency ?? ctx.baseLatency ?? 0) * 1000);
      await api.functions.invoke('twilioAudioTiming', {
        callId, stage: 'heard', bridgedAt, ...(heardAt !== null ? { heardAt } : {}),
        ...(clock ?? {}), ...path, outputMs,
      });
    } finally {
      void ctx.close().catch(() => undefined);
    }
  } catch {
    /* measurement only */
  }
}
